/// <amd-module name="Repos/Views/PullRequest/Components/PullRequestDetails/UpdatesTab" />
import * as React from "react";
import { ICommit } from "Repos/Common/Contracts/History";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IObservableArrayEventArgs, IReadonlyObservableArray, ObservableArray } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { Timeline, TimelineSequence } from "VSSUI/Timeline";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { IIteration } from "../../Contracts/PullRequest";
import { getPullRequestViewService } from "../../PullRequestViewService";
import { IterationCard } from "./IterationCard";

interface IterationUpdate {
    baseCommit?: ICommit;
    iteration?: IIteration;
    showBaseCommit: boolean;
}

export class UpdatesTabContent extends VssComponent<{}> {
    private iterations: IReadonlyObservableArray<IIteration>;
    private updates = new ObservableArray<IterationUpdate>([]);

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        // Retrieve the iterations from the pull request service.
        const prDetailsService = getPullRequestViewService(context.pageContext);
        this.iterations = prDetailsService.getIterations();

        // Translate the incoming iterations to the set we will render.
        this.updates = new ObservableArray(this.translateIterations(this.iterations.value));
    }

    public render() {
        return (
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => (
                    <Observer
                        iterations={{
                            observableValue: this.iterations,
                            filter: (iterationChange: IObservableArrayEventArgs<IIteration>) => {
                                this.updates.value = this.translateIterations(iterationChange.addedItems);
                            }
                        }}
                    >
                        {() => (
                            <Timeline
                                className={css("activity-feed-list", screenSizeProps.screenSize < ScreenSize.small && "bolt-timeline-compact")}
                                itemProvider={this.updates}
                                pageSize={4}
                                renderItem={(rowIndex: number, update: IterationUpdate): [JSX.Element | null, JSX.Element | null] => {
                                    if (update.showBaseCommit) {
                                        return [
                                            <TimelineSequence
                                                className="activity-feed-sequence dot"
                                                hideSuffix={true}
                                                iconProps={{
                                                    render: () => (
                                                        <div className="flex-center justify-center margin-vertical-4">
                                                            <div className="activity-feed-timeline-dot" />
                                                        </div>
                                                    )
                                                }}
                                            />,
                                            <div className="margin-8 margin-bottom-16 rhythm-horizontal-4">
                                                {update.baseCommit ? (
                                                    <>
                                                        <span>{Resources.BasedOnCommit}</span>
                                                        <FPSLink className="monospaced-text" href={update.baseCommit.linkProps.href}>
                                                            {update.baseCommit.shortCommitId}
                                                        </FPSLink>
                                                    </>
                                                ) : (
                                                    <span>{Resources.MissingBaseCommit}</span>
                                                )}
                                            </div>
                                        ];
                                    }

                                    if (update.iteration) {
                                        return [
                                            <TimelineSequence
                                                className="activity-feed-sequence"
                                                hidePrefix={!update.iteration.continuePrevious}
                                                iconProps={{
                                                    render: () => (
                                                        <span className="activity-feed-timeline-iteration body-s font-weight-semibold text-center flex-self-stretch margin-vertical-4">
                                                            {update.iteration!.id.toFixed()}
                                                        </span>
                                                    )
                                                }}
                                            />,
                                            <ScreenSizeObserver>
                                                {(screenSizeProps: { screenSize: ScreenSize }) => (
                                                    // We need a secondary ScreenSizeObserver to allow the element to re-render
                                                    // otherwise the list will use the cached row.
                                                    <IterationCard
                                                        className={css(
                                                            screenSizeProps.screenSize >= ScreenSize.small &&
                                                                "margin-bottom-16 margin-left-8 margin-right-4"
                                                        )}
                                                        iteration={update.iteration!}
                                                    />
                                                )}
                                            </ScreenSizeObserver>
                                        ];
                                    }

                                    return [null, null];
                                }}
                            />
                        )}
                    </Observer>
                )}
            </ScreenSizeObserver>
        );
    }

    /**
     * Creates the list of rows to render: one per iteration, plus one additional row when the iteration
     * is not based on previous one, like a rebase.
     */
    private translateIterations(iterations?: IIteration[]): IterationUpdate[] {
        const updates: IterationUpdate[] = [];

        if (iterations) {
            for (let index = iterations.length - 1; index >= 0; index--) {
                const iteration = iterations[index];

                updates.push({ iteration, showBaseCommit: false });

                if (iteration.showBaseCommit) {
                    updates.push({ baseCommit: iteration.baseCommit, showBaseCommit: true });
                }
            }
        }

        return updates;
    }
}
